import { ShoppingBasketOperationsContextType } from '#src/clientState/providers/ShoppingBasketProvider/types';
import {
  ADD_CONTACT_DETAILS_TO_SHOPPING_BASKET,
  ADD_PRODUCT_TO_SHOPPING_BASKET,
  REMOVE_PRODUCT_FROM_SHOPPING_BASKET,
} from '#src/utils/constants';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import {
  useAddContactDetailsToShoppingBasket,
  useAddProductToShoppingBasket,
  useRemoveProductFromShoppingBasket,
} from './ShoppingBasketProvider.hooks';

interface Props {
  shoppingBasketId?: string;
  children: React.ReactNode;
}

const ShoppingBasketOperationsContext =
  createContext<ShoppingBasketOperationsContextType>({
    addProductToShoppingBasket: () => Promise.resolve(),
    removeProductFromShoppingBasket: () => Promise.resolve(),
    addContactDetailsToShoppingBasket: () => Promise.resolve(),
  });

export const useShoppingBasketOperations = () =>
  useContext(ShoppingBasketOperationsContext);

export const ShoppingBasketOperationsProvider = ({
  shoppingBasketId = '',
  children,
}: Props) => {
  const {
    result: addProductToShoppingBasketResult,
    errors: addProductToShoppingBasketErrors,
    addProductToShoppingBasket,
    loading: addProductToShoppingBasketLoading,
  } = useAddProductToShoppingBasket();

  const {
    result: removeProductFromShoppingBasketResult,
    errors: removeProductFromShoppingBasketErrors,
    removeProductFromShoppingBasket,
    loading: removeProductFromShoppingBasketLoading,
  } = useRemoveProductFromShoppingBasket();

  const {
    result: addContactDetailsToShoppingBasketResult,
    errors: addContactDetailsToShoppingBasketErrors,
    addContactDetailsToShoppingBasket,
    loading: addContactDetailsToShoppingBasketLoading,
  } = useAddContactDetailsToShoppingBasket();

  const addProduct = useCallback(
    ({ productId, productType }) => {
      return addProductToShoppingBasket({
        shoppingBasketId,
        productId,
        productType,
      });
    },
    [addProductToShoppingBasket, shoppingBasketId]
  );

  const removeProduct = useCallback(
    ({ productId, productType }) => {
      return removeProductFromShoppingBasket({
        shoppingBasketId,
        productId,
        productType,
      });
    },
    [removeProductFromShoppingBasket, shoppingBasketId]
  );

  const addContactDetails = useCallback(
    ({ contactDetails }) => {
      return addContactDetailsToShoppingBasket({
        shoppingBasketId,
        contactDetails,
      });
    },
    [addContactDetailsToShoppingBasket, shoppingBasketId]
  );

  useEffect(() => {
    if (
      !addProductToShoppingBasketLoading &&
      addProductToShoppingBasketResult?.addProductToUniversalShoppingBasket
        ?.success === false
    ) {
      throw new Error(ADD_PRODUCT_TO_SHOPPING_BASKET);
    }
  }, [
    addProductToShoppingBasketLoading,
    addProductToShoppingBasketResult,
    addProductToShoppingBasketErrors,
  ]);

  useEffect(() => {
    if (
      !removeProductFromShoppingBasketLoading &&
      removeProductFromShoppingBasketResult
        ?.removeProductFromUniversalShoppingBasket?.success === false
    ) {
      throw new Error(REMOVE_PRODUCT_FROM_SHOPPING_BASKET);
    }
  }, [
    removeProductFromShoppingBasketLoading,
    removeProductFromShoppingBasketResult,
    removeProductFromShoppingBasketErrors,
  ]);

  useEffect(() => {
    if (
      !addContactDetailsToShoppingBasketLoading &&
      addContactDetailsToShoppingBasketResult
        ?.addContactDetailsToUniversalShoppingBasket?.success === false
    ) {
      throw new Error(ADD_CONTACT_DETAILS_TO_SHOPPING_BASKET);
    }
  }, [
    addContactDetailsToShoppingBasketLoading,
    addContactDetailsToShoppingBasketResult,
    addContactDetailsToShoppingBasketErrors,
  ]);

  const value = useMemo(
    () => ({
      addProductToShoppingBasket: addProduct,
      removeProductFromShoppingBasket: removeProduct,
      addContactDetailsToShoppingBasket: addContactDetails,
    }),
    [addProduct, removeProduct, addContactDetails]
  );

  return (
    <ShoppingBasketOperationsContext.Provider value={value}>
      {children}
    </ShoppingBasketOperationsContext.Provider>
  );
};
